import { LANGUAGE_KEY } from '../../translations/message.constants';
import { LessonContentWithTopics } from './sherpath-lesson-content.models';
import { LessonSelection, LessonSelectionError } from './sherpath-lesson-selection.models';

export const LESSON_SELECTION_ERROR_MESSAGE_KEY: Record<LessonSelectionError, LANGUAGE_KEY> = {
  [LessonSelectionError.NO_TOPIC_SELECTED]: LANGUAGE_KEY.LESSON_EDITOR_SELECT_AT_LEAST_ONE_TOPIC,
  [LessonSelectionError.NO_QUESTION_SELECTED]: LANGUAGE_KEY.LESSON_EDITOR_SELECT_AT_LEAST_ONE_QUESTION,
};

export const getInitialSelection = (content: LessonContentWithTopics): LessonSelection => ({
  topicVtwIds: content.topics.map(topic => topic.vtwId),
  questionVtwIds: content.questions.map(question => question.vtwId),
});

// A question with no topic in its own lesson is not governed by topic selection: it is never disabled
// (an empty topic list must not count as "all topics deselected").
export const isQuestionDisabled = (
  questionVtwId: string,
  content: LessonContentWithTopics,
  selectedTopicVtwIds: string[]
): boolean => {
  const questionTopicVtwIds = content.questionTopicIds[questionVtwId];
  return !!questionTopicVtwIds?.length
    && !questionTopicVtwIds.some(topicVtwId => selectedTopicVtwIds.includes(topicVtwId));
};

const inLessonOrder = (vtwIds: string[], orderedVtwIds: string[]): string[] => {
  return orderedVtwIds.filter(vtwId => vtwIds.includes(vtwId));
};

export const toggleTopic = (
  selection: LessonSelection,
  topicVtwId: string,
  content: LessonContentWithTopics
): LessonSelection => {
  const isSelected = selection.topicVtwIds.includes(topicVtwId);
  const topicVtwIds = isSelected
    ? selection.topicVtwIds.filter(selectedTopicVtwId => selectedTopicVtwId !== topicVtwId)
    : inLessonOrder([...selection.topicVtwIds, topicVtwId], content.topics.map(topic => topic.vtwId));

  const questionVtwIds = isSelected
    ? selection.questionVtwIds.filter(questionVtwId => !isQuestionDisabled(questionVtwId, content, topicVtwIds))
    : inLessonOrder(
      [
        ...selection.questionVtwIds,
        ...content.questions
          .filter(question => content.questionTopicIds[question.vtwId]?.includes(topicVtwId))
          .map(question => question.vtwId)
      ],
      content.questions.map(question => question.vtwId)
    );

  return { topicVtwIds, questionVtwIds };
};

export const toggleQuestion = (
  selection: LessonSelection,
  questionVtwId: string,
  content: LessonContentWithTopics
): LessonSelection => {
  if (isQuestionDisabled(questionVtwId, content, selection.topicVtwIds)) {
    return selection;
  }

  const questionVtwIds = selection.questionVtwIds.includes(questionVtwId)
    ? selection.questionVtwIds.filter(selectedQuestionVtwId => selectedQuestionVtwId !== questionVtwId)
    : inLessonOrder([...selection.questionVtwIds, questionVtwId], content.questions.map(question => question.vtwId));

  return { ...selection, questionVtwIds };
};

export const getSelectionError = (selection: LessonSelection): LessonSelectionError | null => {
  if (!selection.topicVtwIds.length) {
    return LessonSelectionError.NO_TOPIC_SELECTED;
  }
  if (!selection.questionVtwIds.length) {
    return LessonSelectionError.NO_QUESTION_SELECTED;
  }
  return null;
};

const hasSameVtwIds = (vtwIds: string[], otherVtwIds: string[]): boolean => {
  return vtwIds.length === otherVtwIds.length && vtwIds.every(vtwId => otherVtwIds.includes(vtwId));
};

export const isSameSelection = (selection: LessonSelection, otherSelection: LessonSelection): boolean => {
  return hasSameVtwIds(selection.topicVtwIds, otherSelection.topicVtwIds)
    && hasSameVtwIds(selection.questionVtwIds, otherSelection.questionVtwIds);
};
